import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import typesafeService from '../../services/typesafeService';

/**
 * All state and side effects for the Effort Management panel: loading the
 * tickets that lack an estimate, inline editing, single-ticket AI estimation
 * and the sequential batch run.
 *
 * @param {(message: string, severity?: string) => void} notify snackbar reporter
 */
export default function useEffortManagement(notify) {
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [editedEffortValue, setEditedEffortValue] = useState('');
  const [savingEffort, setSavingEffort] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);

  /** Filtered client-side: 0 counts as "no estimate", matching the model default. */
  const loadTickets = useCallback(async () => {
    try {
      setLoadingTickets(true);
      const response = await api.get('/tickets');
      const allTickets = response.data.tickets || [];
      setTickets(
        allTickets.filter(
          (ticket) =>
            ticket.estimatedEffort === null ||
            ticket.estimatedEffort === undefined ||
            ticket.estimatedEffort === 0
        )
      );
    } catch (error) {
      console.error('Error loading tickets without effort:', error);
      notify('Failed to load tickets: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoadingTickets(false);
    }
  }, [notify]);

  useEffect(() => {
    if (open) loadTickets();
  }, [open, loadTickets]);

  const startEdit = (ticketId, currentValue) => {
    setEditingTicketId(ticketId);
    setEditedEffortValue(currentValue || '');
  };

  const cancelEdit = () => {
    setEditingTicketId(null);
    setEditedEffortValue('');
  };

  const saveEffort = async (ticketKey) => {
    try {
      setSavingEffort(true);
      const effortValue = parseFloat(editedEffortValue);
      if (isNaN(effortValue) || effortValue < 0) {
        notify('Please enter a valid effort value (positive number)', 'error');
        return;
      }

      await api.patch(`/tickets/${ticketKey}/effort`, { estimatedEffort: effortValue });

      setTickets((prev) =>
        prev.map((ticket) =>
          ticket.key === ticketKey ? { ...ticket, estimatedEffort: effortValue } : ticket
        )
      );
      cancelEdit();
      notify(`Effort updated for ticket ${ticketKey}`, 'success');

      if (effortValue > 0) loadTickets();
    } catch (error) {
      console.error('Error saving ticket effort:', error);
      notify('Failed to save effort: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setSavingEffort(false);
    }
  };

  const estimateOne = async (ticket) => {
    try {
      setSavingEffort(true);
      const estimatedEffort = await typesafeService.estimateEffort(ticket);
      if (estimatedEffort) {
        setEditingTicketId(ticket.key);
        setEditedEffortValue(estimatedEffort.toString());
        notify(`AI estimated ${estimatedEffort} hours for ${ticket.key}`, 'info');
      }
    } catch (error) {
      console.error('Error estimating with Ollama:', error);
      notify(`Failed to get AI estimation: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setSavingEffort(false);
    }
  };

  /** Sequential so the local model isn't swamped; a failed ticket is skipped. */
  const estimateAll = async () => {
    if (!tickets.length) {
      notify('No tickets without effort estimates found', 'info');
      return;
    }
    if (
      !window.confirm(
        `This will estimate effort for ${tickets.length} tickets using AI. Continue?`
      )
    ) {
      return;
    }

    setBatchProcessing(true);
    setBatchProgress(0);
    try {
      for (let i = 0; i < tickets.length; i++) {
        const ticket = tickets[i];
        try {
          const estimatedEffort = await typesafeService.estimateEffort(ticket);
          if (estimatedEffort) {
            await api.put(`/tickets/${ticket._id}`, { estimatedEffort });
          }
        } catch (error) {
          console.error(`Error processing ticket ${ticket.key}:`, error);
        }
        setBatchProgress(Math.round(((i + 1) / tickets.length) * 100));
      }
      await loadTickets();
      notify(`Successfully processed ${tickets.length} tickets`, 'success');
    } catch (error) {
      console.error('Error in batch processing:', error);
      notify(`Batch processing error: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setBatchProcessing(false);
    }
  };

  const visibleTickets = tickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return {
    open,
    setOpen,
    tickets,
    visibleTickets,
    loadingTickets,
    onRefresh: loadTickets,
    page,
    rowsPerPage,
    onPageChange: (event, newPage) => setPage(newPage),
    onRowsPerPageChange: (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    editingTicketId,
    editedEffortValue,
    setEditedEffortValue,
    onStartEdit: startEdit,
    onCancelEdit: cancelEdit,
    onSave: saveEffort,
    savingEffort,
    onEstimateOne: estimateOne,
    onEstimateAll: estimateAll,
    batchProcessing,
    batchProgress,
  };
}
